import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import { BEWalletTypeFormat, WalletTypeListItem } from "types/wallets";
import { getEnvVariable } from "utilities/env";

export const useWallets = () => {
  const { code: tenant } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useWalletTypeList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<WalletTypeListItem>, any> => {
    const link = tenant
      ? `${getEnvVariable(
          "REACT_APP_API_URL",
        )}/${tenant}/walletType${querystring(props)}`
      : "";
    return useSWRImmutable(link, () => fetcher(link));
  };

  const useInfiniteWalletsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<WalletTypeListItem>>(
      (index) =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/walletType${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const addWalletType = async (payload: BEWalletTypeFormat) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/walletType`,
      {
        body: JSON.stringify({
          walletType: payload,
        }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editWalletType = async (
    walletTypeId: string,
    payload: BEWalletTypeFormat,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/walletType/${walletTypeId}`,
      {
        body: JSON.stringify({ walletType: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useWalletTypeData = (
    walletTypeId: string,
  ): SWRResponse<BEWalletTypeFormat, any> => {
    return useSWR(
      () =>
        walletTypeId && tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/walletType/${walletTypeId}`
          : null,
      fetcher,
    );
  };

  const getWalletTypeBatchData = async (values: { walletTypeId: string }[]) => {
    if (!tenant) return;

    const responses = await Promise.all(
      values.map(({ walletTypeId }) =>
        fetcher(
          `${getEnvVariable(
            "REACT_APP_API_URL",
          )}/${tenant}/walletType/${walletTypeId}`,
          {
            headers: { "Content-Type": "application/json" },
            method: "GET",
          },
        ),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) => {
        return resp.map(({ walletTypeId, createdAt, isDefault, ...rest }) => ({
          ...rest,
        }));
      });

    return responses;
  };

  const addWalletTypeBatch = async (values: BEWalletTypeFormat[]) => {
    if (!tenant) return;

    const responses = await Promise.all(
      values.map(({ walletTypeId, ...rest }) =>
        walletTypeId
          ? new Promise((resolve) =>
              resolve({
                walletTypeId,
                ...rest,
              }),
            )
          : fetcher(
              `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/walletType`,
              {
                body: JSON.stringify({
                  walletType: { ...rest, active: false },
                }),
                headers: { "Content-Type": "application/json" },
                method: "POST",
              },
            ),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) =>
        resp.map((r, i) => ({
          ...r,
          data: values[i],
        })),
      );

    return responses;
  };

  return {
    addWalletType,
    addWalletTypeBatch,
    editWalletType,
    getWalletTypeBatchData,
    useWalletTypeData,
    useWalletTypeList,
    useInfiniteWalletsList,
  };
};
